<template>
  <NewBoardItemDialog
    :dialog="renameDialog"
    :title="renameDialogTitle"
    :initial-name="renameInitialName"
    @close="renameDialog = false"
    @save="saveRename"
  />

  <v-app-bar app color="primary" dark dense elevation="8" class="app-bar" >
    <v-img
      class="mx-2"
      :src="betaImg"
      max-height="35"
      max-width="35"
      contain
      style="max-width: 35px; width: 100%; height: auto"
    ></v-img>
    <v-img
      class="mx-2 app-bar__logo"
      :src="logoImg"
      max-height="65"
      max-width="85"
      contain
    ></v-img>
    <PracticeLocaleSwitch
      v-if="showLocaleSwitch"
      bar
      class="app-bar__locale"
    />
    <v-toolbar-title class="app-bar__title">
      <span class="app-bar__brand d-none d-lg-inline">
        <template v-if="isHebrew">לוח <strong style="color: darkorange">מתמטיקה מקוון</strong></template>
        <template v-else>ONLINE <strong style="color: darkorange">MATH WHITEBOARD</strong></template>
      </span>
      <div
        v-if="boardContext.level !== 'none'"
        class="app-bar__context"
      >
        <v-chip
          size="x-small"
          :color="boardContext.chipColor"
          variant="flat"
          class="app-bar__chip"
        >
          {{ boardChromeText(boardContext.chipLabel, chromeHebrew) }}
        </v-chip>
        <span class="app-bar__breadcrumb">
          <template
            v-for="(crumb, index) in boardContext.breadcrumbs"
            :key="`${crumb.text}-${index}`"
          >
            <button
              v-if="isRenameableCrumb(index)"
              type="button"
              class="app-bar__crumb-link app-bar__crumb-rename"
              data-cy="app-bar-rename-crumb"
              :aria-label="`Rename ${boardContext.level}`"
              :title="`Rename ${boardContext.level}`"
              @click="openRenameFromCrumb(index)"
            >
              {{ boardChromeText(crumb.text, chromeHebrew) }}
            </button>
            <router-link
              v-else-if="crumb.to"
              :to="crumb.to"
              class="app-bar__crumb-link"
            >
              {{ boardChromeText(crumb.text, chromeHebrew) }}
            </router-link>
            <span v-else class="app-bar__crumb-text">{{ boardChromeText(crumb.text, chromeHebrew) }}</span>
            <span
              v-if="index < boardContext.breadcrumbs.length - 1"
              class="app-bar__sep"
              aria-hidden="true"
            >
              ›
            </span>
          </template>
        </span>
      </div>
    </v-toolbar-title>

    <!-- Media bar temporarily disabled (TURN not configured). -->
    <!-- <lessonMediaBar></lessonMediaBar> -->

    <!-- lessons -->
    <v-tooltip :text="shell.lessons" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          data-cy="lessons"
          v-show="showLessons"
          icon
          v-on:click="navToLessons"
          v-bind="props"
        >
          <v-icon>mdi-archive-edit-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <!-- practice (question bank, no lesson) -->
    <v-tooltip :text="ui.practiceTooltip" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          data-cy="practice"
          v-show="showPractice"
          icon
          to="/practice"
          v-bind="props"
        >
          <v-icon>mdi-school-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <!-- questions -->
    <v-tooltip :text="shell.questions" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-show="showQuestions"
          icon
          v-on:click="navToQuestions"
          v-bind="props"
        >
          <v-icon>mdi-message-question-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <!-- answers -->
    <v-tooltip :text="shell.answers" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-show="showAnswers"
          icon
          v-on:click="navToAnswers"
          v-bind="props"
        >
          <v-icon>mdi-checkbox-marked-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-tooltip
      v-if="showLessonInvite"
      :text="inviteTooltip"
      location="bottom"
    >
      <template v-slot:activator="{ props: inviteProps }">
        <v-btn
          id="invite-btn"
          v-bind="inviteProps"
          class="app-bar__invite-btn d-none d-sm-flex"
          color="orange"
          variant="flat"
          size="small"
          prepend-icon="mdi-link-variant"
          @click="openInviteDialog"
        >
          <span class="d-none d-md-inline">{{ shell.invite }}</span>
        </v-btn>
      </template>
    </v-tooltip>

    <v-btn
      v-if="showEndLesson"
      id="end-lesson-btn"
      data-cy="end-lesson-btn"
      class="app-bar__end-lesson-btn"
      :color="lessonIsEnded ? 'success' : 'error'"
      variant="flat"
      size="small"
      :prepend-icon="lessonIsEnded ? 'mdi-play' : 'mdi-stop'"
      :aria-label="lessonIsEnded ? shell.reopenLesson : shell.endLesson"
      @click="onEndLessonClick"
    >
      <span class="d-none d-md-inline">
        {{ lessonIsEnded ? shell.reopenLesson : shell.endLesson }}
      </span>
    </v-btn>

    <!-- students -->
    <span
      v-if="showOnlineStudents"
      class="app-bar__students-label d-none d-lg-inline"
    >
      {{ shell.studentsLabel }}
    </span>
    <v-tooltip
      :text="shell.onlineStudentsTip"
      location="bottom"
    >
      <template v-slot:activator="{ props: tooltipProps }">
        <v-badge
          :content="onlineStudentsCount"
          :model-value="onlineStudentsCount > 0"
          color="green"
          overlap
        >
          <v-btn
            id="online-students-btn"
            data-cy="online_students_btn"
            v-show="showOnlineStudents"
            icon
            class="app-bar__students-btn"
            :class="{
              'app-bar__students-btn--highlight': highlightOnlineStudents,
            }"
            :aria-label="shell.onlineStudents"
            v-on:click="showOnlineStudentsDialog"
            v-bind="tooltipProps"
          >
            <v-icon>mdi-account-school-outline</v-icon>
          </v-btn>
        </v-badge>
      </template>
    </v-tooltip>

    <v-tooltip :text="ui.help" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-show="!sharedGuest && (user || isPracticeRoute)"
          id="help-btn"
          icon
          v-bind="props"
          :aria-label="ui.help"
          @click="openHelp"
        >
          <v-icon>mdi-help-circle-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-tooltip :text="shell.feedbackTip" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-if="!sharedGuest"
          data-cy="send_feedback"
          v-bind="props"
          class="feedback-btn"
          color="orange"
          variant="flat"
          size="small"
          @click="emit('show-feedback')"
        >
          <v-icon start>mdi-message-outline</v-icon>
          <span class="d-none d-md-inline">{{ shell.feedback }}</span>
        </v-btn>
      </template>
    </v-tooltip>

    <v-btn
      v-show="showClassroomHomeLink"
      variant="text"
      class="text-none"
      data-cy="classroom_home_btn"
      @click="navToHome"
    >
      {{ shell.classroom }}
    </v-btn>

    <!-- sign in / register -->
    <v-tooltip :text="shell.signInTeacher" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          v-show="showClassroomSignIn"
          icon
          data-cy="app-bar-signin-teacher"
          v-on:click="showLoginDialog('TEACHER')"
        >
          <v-icon>mdi-account-tie</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-tooltip :text="shell.signInStudent" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          v-show="showClassroomSignIn"
          icon
          data-cy="app-bar-signin-student"
          v-on:click="showLoginDialog('STUDENT')"
        >
          <v-icon>mdi-account-school-outline</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-divider
      v-if="!sharedGuest"
      class="app-bar__account-divider"
      vertical
    ></v-divider>

    <!-- user image or name -->
    <v-tooltip bottom hidden>
      <template v-slot:activator="{ props }">
        <v-avatar v-show="!sharedGuest && user?.imageUrl" size="36px"
          ><img v-bind:src="user?.imageUrl"
        /></v-avatar>
      </template>
      <span v-show="user?.firstName">{{ user?.firstName }}</span>
    </v-tooltip>

    <span
      v-show="!sharedGuest && user?.firstName && !user?.imageUrl"
      class="app-bar__hello"
      >{{ isHebrew ? "שלום" : "Hello" }} {{ user?.firstName }}</span
    >

    <v-tooltip :text="shell.signOut" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          v-show="!sharedGuest && user"
          class="app-bar__signout"
          icon
          v-on:click="signOut"
          v-bind="props"
        >
          <v-icon>mdi-logout</v-icon>
        </v-btn>
      </template>
    </v-tooltip>
  </v-app-bar>
</template>

<script setup lang="ts">
import betaImg from "@/assets/beta.png";
import logoImg from "@/assets/logo.png";
import { useRouter, useRoute } from "vue-router";
import { COLLABORATION } from "../constants/helpCopy";
import { computed, ref } from "vue";
import { useUserStore } from "../store/pinia/userStore";
import { useBoardContextStore } from "../store/pinia/boardContextStore";
import { useLessonStore } from "../store/pinia/lessonStore";
import { useQuestionStore } from "../store/pinia/questionStore";
import { useGlobalAlertStore } from "../store/pinia/globalAlertStore";
import NewBoardItemDialog from "./NewBoardItemDialog.vue";
import { useUiHintStore } from "../store/pinia/uiHintStore";
import { storeToRefs } from "pinia";
import { useEditModeStore } from "../store/pinia/editModeStore";
import { useStudentStore } from "../store/pinia/studentStore";
import { useCookies } from "vue3-cookies";
import { useOnboardingStore } from "../store/pinia/onboardingStore";
import { ACCESS_TOKEN_NAME } from "common/globals";
import { usePracticeLocale } from "../composables/usePracticeLocale";
import PracticeLocaleSwitch from "./PracticeLocaleSwitch.vue";
import { boardChromeText, practiceUi, shellUi } from "../i18n/practiceCopy";
import { useLessonSessionStore } from "../store/pinia/lessonSessionStore";
import useUserOutgoingOperations from "../helpers/userOutgoingOperationsHelper";
// import LessonMediaBar from "./LessonMediaBar.vue";

const emit = defineEmits<{
  "show-feedback": [];
}>();

const cookies = useCookies().cookies;
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const inviteTooltip = COLLABORATION.inviteTooltip;
const boardContext = useBoardContextStore();
const lessonStore = useLessonStore();
const questionStore = useQuestionStore();
const globalAlertStore = useGlobalAlertStore();
const uiHintStore = useUiHintStore();
const renameDialog = ref(false);
const renameDialogTitle = ref("");
const renameInitialName = ref("");
const renameTargetUuid = ref("");
const renameTargetType = ref<"lesson" | "question">("lesson");
const { highlightOnlineStudentsBtn: highlightOnlineStudents } =
  storeToRefs(uiHintStore);
const editModeStrore = useEditModeStore();
const studentStore = useStudentStore();
const lessonSessionStore = useLessonSessionStore();
const userOutgoingOperations = useUserOutgoingOperations();
const onboardingStore = useOnboardingStore();

const onlineStudentsCount = computed(() => {
  return studentStore.getStudents().length;
});

const user = computed(() => {
  return userStore.getCurrentUser();
});

const sharedGuest = computed(() => route.name === "practiceTogether");

const showLessons = computed(() => {
  return user.value && !sharedGuest.value;
});

const showQuestions = computed(() => {
  return !!userStore.getCurrentUser() && !sharedGuest.value;
});

const showPractice = computed(() => {
  return !sharedGuest.value;
});

const isPracticeRoute = computed(() => {
  const name = route.name;
  return (
    name === "practice" ||
    name === "practiceHe" ||
    name === "practiceBlank" ||
    name === "practiceQuestion"
  );
});
const { isHebrew, hebrewOptionAvailable } = usePracticeLocale();
const showLocaleSwitch = computed(
  () =>
    !sharedGuest.value &&
    (hebrewOptionAvailable.value ||
      route.name === "practice" ||
      route.name === "practiceHe"),
);
const chromeHebrew = isHebrew;
const ui = computed(() => practiceUi(isHebrew.value));
const shell = computed(() => shellUi(isHebrew.value));

const showClassroomSignIn = computed(
  () => !user.value && !sharedGuest.value,
);

const showClassroomHomeLink = computed(
  () => !user.value && isPracticeRoute.value && !sharedGuest.value,
);

const showAnswers = computed(() => {
  return isTeacher.value && !sharedGuest.value;
});

const showOnlineStudents = computed(() => {
  return isTeacher.value && !sharedGuest.value;
});

const isTeacher = computed(() => userStore.isTeacher());

const showLessonInvite = computed(
  () => route.name === "lesson" && isTeacher.value,
);

const currentLesson = computed(() => lessonStore.getCurrentLesson());

const ownsCurrentLesson = computed(() => {
  const lesson = currentLesson.value;
  const current = user.value;
  if (!lesson || !current) return false;
  if (lesson.user?.uuid && lesson.user.uuid === current.uuid) return true;
  const lessonUserId = (lesson as { userId?: number }).userId;
  return lessonUserId != null && current.id != null && lessonUserId === current.id;
});

const showEndLesson = computed(
  () => route.name === "lesson" && ownsCurrentLesson.value,
);

const lessonIsEnded = computed(() =>
  lessonSessionStore.isEnded(currentLesson.value?.uuid),
);

async function setLessonEnded(ended: boolean) {
  const lesson = currentLesson.value;
  if (!lesson?.uuid) return;
  await userOutgoingOperations.syncOutgoingLessonSession(lesson.uuid, ended);
  lessonSessionStore.setEnded(lesson.uuid, ended);
}

function onEndLessonClick() {
  if (lessonIsEnded.value) {
    void setLessonEnded(false);
    return;
  }
  globalAlertStore.open(
    shell.value.endLesson,
    shell.value.endLessonConfirm,
    "warning",
    () => {
      void setLessonEnded(true);
    },
  );
}

function openInviteDialog() {
  uiHintStore.requestAccessLinkDialog();
}

function openHelp() {
  onboardingStore.openHelpDrawer();
}

function showLoginDialog(userType?: string) {
  const query: Record<string, string> = {};
  if (userType) query.userType = userType;
  if (isPracticeRoute.value) query.from = route.fullPath;
  router.push({ name: "login", query });
}

function signOut() {
  boardContext.clear();
  userStore.setCurrentUser(null);
  void import("../store/pinia/practiceMasteryStore").then(
    ({ syncPracticeProgress }) => syncPracticeProgress(null),
  );
  cookies.remove(ACCESS_TOKEN_NAME);
  router.push("/");
}

function showOnlineStudentsDialog() {
  editModeStrore.setEditMode("STUDENTS_MONITORING");
}

function navToHome() {
  router.push({ name: "main" });
}

function navToLessons() {
  router.push("/lessons");
}

function navToQuestions() {
  router.push("/questions");
}

function navToAnswers() {
  router.push("/answers");
}

function isRenameableCrumb(index: number): boolean {
  if (!isTeacher.value) {
    return false;
  }
  const last = boardContext.breadcrumbs.length - 1;
  if (index !== last) {
    return false;
  }
  return boardContext.level === "lesson" || boardContext.level === "question";
}

function openRenameFromCrumb(index: number) {
  const crumb = boardContext.breadcrumbs[index];
  if (!crumb || !isRenameableCrumb(index)) {
    return;
  }

  if (boardContext.level === "lesson") {
    const lessonUUId =
      (route.params.lessonUUId as string | undefined) ??
      crumb.to?.params?.lessonUUId;
    if (!lessonUUId) {
      return;
    }
    renameTargetType.value = "lesson";
    renameTargetUuid.value = lessonUUId;
    renameDialogTitle.value = "<span>Rename <strong>lesson</strong></span>";
  } else {
    const questionUUId =
      (route.params.questionUUId as string | undefined) ??
      crumb.to?.params?.questionUUId;
    if (!questionUUId) {
      return;
    }
    renameTargetType.value = "question";
    renameTargetUuid.value = questionUUId;
    renameDialogTitle.value = "<span>Rename <strong>question</strong></span>";
  }

  renameInitialName.value = crumb.text;
  renameDialog.value = true;
}

async function saveRename(name: string) {
  const trimmed = name?.trim();
  if (!trimmed || !renameTargetUuid.value) {
    return;
  }
  renameDialog.value = false;

  try {
    if (renameTargetType.value === "lesson") {
      const updated = await lessonStore.updateLesson(
        renameTargetUuid.value,
        trimmed,
      );
      boardContext.setLesson(updated.name, updated.uuid);
    } else {
      const updated = await questionStore.updateQuestion(
        renameTargetUuid.value,
        trimmed,
      );
      if (updated.lesson) {
        boardContext.setQuestion(
          updated.lesson.name,
          updated.lesson.uuid,
          updated.name,
          updated.uuid,
        );
      } else if (updated.practice) {
        boardContext.setPracticeQuestionEditor(
          updated.practice.subject,
          updated.name,
          updated.uuid,
        );
      }
    }
  } catch (error) {
    globalAlertStore.open(
      "Rename failed",
      (error as Error).message,
      "error",
      () => {},
    );
  }
}
</script>

<style scoped>
.app-bar__title {
  overflow: hidden;
  min-width: 0;
}

.app-bar__brand {
  white-space: nowrap;
  margin-inline-end: 12px;
}

.app-bar__context {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  margin-inline-start: 8px;
}

.app-bar__chip {
  flex-shrink: 0;
  font-weight: 700;
}

.app-bar__breadcrumb {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.95rem;
}

.app-bar__crumb-link {
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
}

.app-bar__crumb-link:hover {
  text-decoration: underline;
}

.app-bar__crumb-rename {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
}

.app-bar__crumb-text {
  color: rgba(255, 255, 255, 0.85);
}

.app-bar__sep {
  margin: 0 4px;
  opacity: 0.75;
}

.app-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding-top: env(safe-area-inset-top);
}

.feedback-btn {
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: none;
}

.app-bar__students-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
  margin-inline-end: 2px;
}

.app-bar__invite-btn {
  margin-inline-end: 4px;
}

.app-bar__locale {
  flex: 0 0 auto;
  margin-inline: 4px;
  z-index: 1;
}

.app-bar__account-divider {
  margin-inline: 8px;
}

.app-bar__hello {
  white-space: nowrap;
}

.app-bar__signout {
  flex: 0 0 auto;
}

@media (max-width: 720px) {
  .app-bar__hello {
    display: none !important;
  }

  .app-bar__account-divider {
    margin-inline: 2px;
  }
}

.app-bar :deep(.v-toolbar__content) {
  overflow: visible;
}

.app-bar__students-btn--highlight {
  animation: students-btn-pulse 1.1s ease-in-out 3;
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.95);
  border-radius: 50%;
}

@keyframes students-btn-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.12);
  }
}

@media (max-width: 600px) {
  .app-bar__logo {
    display: none;
  }
}

@media (max-width: 1023px) {
  .app-bar__context {
    margin-inline-start: 0;
    max-width: min(46vw, 220px);
  }

  .app-bar__breadcrumb {
    font-size: 0.85rem;
  }

  .app-bar :deep(.v-toolbar__content) {
    padding-inline: 4px;
  }

  .app-bar :deep(.v-btn--icon) {
    min-width: 44px;
    min-height: 44px;
  }
}
</style>
